const fetchWithTimeout = require('../auth/fetchWithTimeout');

const isReference = (value) => value !== null
    && typeof value === 'object'
    && typeof value.metaId === 'string'
    && value.metaId.trim().length > 0
    && typeof value.mediaType === 'string'
    && value.mediaType.trim().length > 0
    && typeof value.addedAt === 'string';

class CarolineLibraryHttpError extends Error {
    constructor(status) {
        super(`Caroline Library request failed with HTTP ${status}`);
        this.name = 'CarolineLibraryHttpError';
        this.status = status;
    }
}

class CarolineLibraryProtocolError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CarolineLibraryProtocolError';
    }
}

const normalizeInput = (reference) => {
    const metaId = reference?.metaId;
    const mediaType = reference?.mediaType;
    if (typeof metaId !== 'string' || typeof mediaType !== 'string'
        || metaId.trim().length === 0 || mediaType.trim().length === 0) {
        throw new TypeError('Caroline Library references require non-empty mediaType and metaId strings.');
    }
    return { metaId: metaId.trim(), mediaType: mediaType.trim() };
};

const createCarolineLibraryClient = (fetchImplementation = (...args) => fetch(...args)) => {
    const request = async (url, options) => {
        const response = await fetchWithTimeout(url, {
            ...options,
            credentials: 'include',
        }, fetchImplementation);
        if (!response.ok) {
            throw new CarolineLibraryHttpError(response.status);
        }
        return response;
    };

    const readJson = async (response) => {
        try {
            return await response.json();
        } catch {
            throw new CarolineLibraryProtocolError('Caroline Library returned invalid JSON.');
        }
    };

    return {
        async getLibrary() {
            const response = await request('/api/library', {
                method: 'GET',
                headers: { accept: 'application/json' },
            });
            const body = await readJson(response);
            if (body === null || typeof body !== 'object' || !Array.isArray(body.items) || !body.items.every(isReference)) {
                throw new CarolineLibraryProtocolError('Caroline Library returned an invalid item list.');
            }
            return body.items.map(({ metaId, mediaType, addedAt }) => ({ metaId, mediaType, addedAt }));
        },

        async addLibraryItem({ metaId, mediaType }) {
            const reference = normalizeInput({ metaId, mediaType });
            const response = await request('/api/library', {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                },
                body: JSON.stringify(reference),
            });
            const body = await readJson(response);
            if (body === null || typeof body !== 'object' || !isReference(body.item)) {
                throw new CarolineLibraryProtocolError('Caroline Library returned an invalid item.');
            }
            const { item } = body;
            if (item.metaId !== reference.metaId || item.mediaType !== reference.mediaType) {
                throw new CarolineLibraryProtocolError('Caroline Library returned a different reference than requested.');
            }
            return { metaId: item.metaId, mediaType: item.mediaType, addedAt: item.addedAt };
        },

        async removeLibraryItem(input) {
            const { metaId, mediaType } = normalizeInput(input);
            await request(`/api/library/${encodeURIComponent(mediaType)}/${encodeURIComponent(metaId)}`, {
                method: 'DELETE',
                headers: { accept: 'application/json' },
            });
        },
    };
};

const carolineLibraryClient = createCarolineLibraryClient();

module.exports = {
    CarolineLibraryHttpError,
    CarolineLibraryProtocolError,
    carolineLibraryClient,
    createCarolineLibraryClient,
};
